import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWidgets, deleteWidget, updateWidgetTitle, refreshWidget } from '../../../../app/store/slices/adminSlice';
import { fetchUserWidgets, deleteUserWidget, updateUserWidgetTitle, refreshUserWidget } from '../../../../app/store/slices/userDashboardSlice';
import ConfirmationModal from '../modals/ConfirmationModal';
import { toast } from 'react-toastify';
import EditTitleModal from '../modals/EditTitleModal';
import { shouldSyncWithBackend, getUserId } from '../../../../utils/widgetPersistence';
import ChartWidget from '../chart-table/ChartWidget';
import MetricWidget from '../chart-table/MetricWidget';
import TableWidget from '../chart-table/TableWidget';
import ListWidget from '../chart-table/ListWidget';

const WidgetDisplay = ({ 
  widget, 
  compact = false, 
  context = 'admin', 
  canvasId = null,
  isResizable = false
}) => {
  const dispatch = useDispatch();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editTitle, setEditTitle] = useState(widget?.title || widget?.config?.title || 'Untitled Widget');
  const [localWidget, setLocalWidget] = useState(widget);
  const isMounted = useRef(true);
  const effectiveCanvasId = canvasId || widget?.canvasId;
 
  useEffect(() => {
    if (widget && JSON.stringify(widget) !== JSON.stringify(localWidget)) {
      setLocalWidget(widget);
      setEditTitle(widget?.title || widget?.config?.title || 'Untitled Widget');
    }
  }, [widget, localWidget]);
  
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const clearWidgetStorage = useCallback(() => {
    if (context === 'admin') {
      Object.keys(localStorage).forEach(key => {
        if (key.includes('admin_widgets')) {
          localStorage.removeItem(key);
        }
      });
    } else if (context === 'user' && effectiveCanvasId) {
      Object.keys(localStorage).forEach(key => {
        if (key.includes('user_widgets') && key.includes(effectiveCanvasId)) {
          localStorage.removeItem(key);
        }
      });
    }
  }, [context, effectiveCanvasId]);

  // Handle removing a widget
  const handleRemove = () => {
    setShowDeleteModal(true);
  };

  // Confirm widget deletion
  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    
    try {
      if (context === 'admin') {
        await dispatch(deleteWidget(localWidget.id || localWidget._id)).unwrap();
        clearWidgetStorage();
        dispatch(fetchWidgets({ forceRefresh: true }));
      } else if (context === 'user') {
        if (!effectiveCanvasId) {
          throw new Error('Canvas ID is required to delete user widget');
        }
        
        await dispatch(deleteUserWidget({
          widgetId: localWidget?.id || localWidget?._id,
          canvasId: effectiveCanvasId
        })).unwrap();
        
        clearWidgetStorage();
        dispatch(fetchUserWidgets({ 
          canvasId: effectiveCanvasId,
          forceRefresh: true
        }));
      }
      
      toast.success(`Widget "${localWidget?.title || 'Untitled Widget'}" has been successfully deleted.`);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting widget:', error);
      toast.error(`Failed to delete widget: ${error?.message || 'Unknown error occurred'}`);
    } finally {
      if (isMounted.current) {
        setIsDeleting(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  // Handle opening edit modal
  const handleEdit = () => {
    setEditTitle(localWidget?.title || localWidget?.config?.title || 'Untitled Widget');
    setShowEditModal(true);
  };

  // Handle widget title updates
  const handleTitleUpdate = async () => {
    const originalTitle = localWidget?.title;
    
    setLocalWidget(prev => ({
      ...prev,
      title: editTitle
    }));
    
    try {
      if (context === 'admin') {
        await dispatch(updateWidgetTitle({
          id: localWidget?.id || localWidget?._id,
          updateData: { title: editTitle }
        })).unwrap();
        
        clearWidgetStorage();
        dispatch(fetchWidgets({ forceRefresh: true }));
      } else if (context === 'user') {
        if (!effectiveCanvasId) {
          throw new Error('Canvas ID is required to update user widget');
        }
        
        await dispatch(updateUserWidgetTitle({
          widgetId: localWidget?.id || localWidget?._id,
          canvasId: effectiveCanvasId,
          updateData: { title: editTitle }
        })).unwrap();
        
        clearWidgetStorage();
        dispatch(fetchUserWidgets({ 
          canvasId: effectiveCanvasId,
          forceRefresh: true
        }));
      }
      
      toast.success('Widget title updated successfully.');
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating widget title:', error);
      
      setLocalWidget(prev => ({
        ...prev,
        title: originalTitle
      }));
      
      toast.error(`Failed to update widget title: ${error?.message || 'Unknown error occurred'}`);
    }
  };

  // Handle widget refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    try {
      if (context === 'admin') {
        const refreshedWidget = await dispatch(refreshWidget(localWidget?.id || localWidget?._id)).unwrap();
        
        if (refreshedWidget) {
          setLocalWidget(prev => ({
            ...prev,
            data: refreshedWidget.data || refreshedWidget,
            updatedAt: new Date().toISOString()
          }));
        }
        
        clearWidgetStorage();
        dispatch(fetchWidgets({ forceRefresh: true }));
        dispatch(fetchWidgets({ forceRefresh: shouldSyncWithBackend() }));
      } else if (context === 'user') {
        if (!effectiveCanvasId) {
          throw new Error('Canvas ID is required to refresh user widget');
        }
        
        const refreshedWidget = await dispatch(refreshUserWidget({
          widgetId: localWidget?.id || localWidget?._id,
          canvasId: effectiveCanvasId
        })).unwrap();
        
        if (refreshedWidget) {
          setLocalWidget(prev => ({
            ...prev,
            data: refreshedWidget.data || refreshedWidget,
            lastUpdated: new Date().toISOString()
          }));
        }
        
        clearWidgetStorage();
        dispatch(fetchUserWidgets({
          canvasId: effectiveCanvasId,
          forceRefresh: true
        }));
      }
      
      toast.success('Widget data refreshed.');
    } catch (error) {
      console.error('Error refreshing widget:', error);
      toast.error(`Failed to refresh widget: ${error?.message || 'Unknown error occurred'}`);
    } finally {
      if (isMounted.current) {
        setIsRefreshing(false);
      }
    }
  };
  
  const renderWidgetContent = () => {
    const widgetType =
      localWidget?.widgetType?.toLowerCase() ||
      localWidget?.config?.type?.toLowerCase() ||
      localWidget?.data?.type?.toLowerCase() ||
      localWidget?.type?.toLowerCase() ||
      'chart';

    switch (widgetType) {
      case 'metric':
        return <MetricWidget metric={localWidget?.data?.metric || localWidget?.data} compact={compact} isResizable={isResizable} />;
      case 'table':
        return <TableWidget table={localWidget?.data?.table || localWidget?.data} isResizable={isResizable} />;
      case 'list':
        return <ListWidget list={localWidget?.data?.list || localWidget?.data} isResizable={isResizable} />;
      case 'chart':
      case 'bar':
      case 'line':
      case 'pie':
      case 'doughnut':
      case 'polar':
      case 'radar':
      default:
        return <ChartWidget widget={localWidget} compact={compact} isResizable={isResizable} />;
    }
  };

  const containerClass = compact 
    ? "bg-white rounded-lg shadow border p-3" 
    : `bg-white rounded-lg shadow-md border p-4 ${isResizable ? 'h-full' : ''}`
  return (
    <>
      <div 
        className={`${containerClass} flex flex-col ${isResizable ? 'h-full' : 'min-h-[200px]'}`}
        data-canvas-id={localWidget?.canvasId || effectiveCanvasId || 'unknown'}
        data-widget-id={localWidget?.id || localWidget?._id}
      >
        {/* Header */}
        <div className={`flex items-center justify-between ${isResizable ? 'mb-2' : 'mb-3'}`}>
          <h4 className={`font-medium text-gray-800 ${compact ? 'text-sm' : 'text-base'} ${isResizable ? 'pl-6' : ''} truncate`}>
            {localWidget?.title || localWidget?.config?.title || 'Untitled Widget'}
          </h4>
          <div className="flex items-center flex-shrink-0">
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              className="text-gray-400 hover:text-blue-500 transition-colors p-1 rounded hover:bg-blue-50 mr-1"
              title="Refresh widget data"
              disabled={isRefreshing}
            >
              <svg className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            
            {/* Edit Button */}
            <button
              onClick={handleEdit}
              className="text-gray-400 hover:text-blue-500 transition-colors p-1 rounded hover:bg-blue-50 mr-1"
              title="Edit widget title"
            >
              <svg className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            
            {/* Delete Button */}
            <button
              onClick={handleRemove}
              className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-red-50"
              title={`Remove ${context === 'admin' ? 'Admin' : 'User'} Widget`}
              disabled={isDeleting}
            >
              <svg className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-grow ${isResizable ? 'overflow-hidden' : 'mt-2'}`}>
          {renderWidgetContent()}
        </div>

        {/* */}
        {!compact && !isResizable && localWidget?.insights && localWidget.insights.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-200">
            <div className="text-xs font-medium text-gray-700 mb-1">Insights</div>
            <div className="space-y-1">
              {localWidget.insights.slice(0, 2).map((insight, index) => (
                <div key={index} className={`text-xs p-2 rounded ${
                  insight.impact === 'high' ? 'bg-red-50 text-red-700' :
                  insight.impact === 'medium' ? 'bg-yellow-50 text-yellow-700' :
                  'bg-blue-50 text-blue-700'
                }`}>
                  <div className="font-medium">{insight.title}</div>
                  <div className="text-gray-600">{insight.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer info */}
        {!compact && isResizable && (
          <div className="text-xs text-gray-500 mt-auto pt-2 text-right border-t flex-shrink-0">
            {localWidget ? (
              
              <>Last Updated: {new Date(widget?.updatedAt || widget?.createdAt).toLocaleTimeString()}</>
            ) : (
              <>Last Updated: {new Date(localWidget?.updatedAt).toLocaleTimeString()}</>
            )}
          </div>
        )}
      </div>

      {/* Render modals using portals to ensure they appear outside containers */}
      {showDeleteModal && createPortal(
        <ConfirmationModal
          show={showDeleteModal}
          title={`Remove ${context === 'admin' ? 'Admin' : 'User'} Widget`}
          message={`Are you sure you want to remove the widget "${localWidget?.title || 'Untitled Widget'}"?`}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          confirmText="Remove Widget"
          cancelText="Cancel"
          loading={isDeleting}
        />,
        document.body
      )}

      {showEditModal && createPortal(
        <EditTitleModal
          show={showEditModal}
          title="Rename Widget"
          currentTitle={editTitle}
          setTitle={setEditTitle}
          onCancel={() => setShowEditModal(false)}
          onConfirm={handleTitleUpdate}
        />,
        document.body
      )}
    </>
  );
};

export default WidgetDisplay;